// Smart Daara - Physical school: timetable management (view, generate, edit, print)
import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { physicalAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { TimetableGrid, minToStr, DAY_LABELS } from '../../components/physical/TimetableGrid';
import { Wand2, Printer, Trash2, Eraser } from 'lucide-react';
import { toast } from 'sonner';

export default function TimetablePage() {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [config, setConfig] = useState(null);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [viewMode, setViewMode] = useState('class'); // class | teacher | room
  const [viewId, setViewId] = useState('');
  const [entries, setEntries] = useState([]);
  const [addDialog, setAddDialog] = useState(null); // {day, start}
  const [addForm, setAddForm] = useState({ class_id: '', subject_id: '', room_id: '', duration: 1 });
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [genReport, setGenReport] = useState(null);
  const [genLoading, setGenLoading] = useState(false);

  const loadStatic = async () => {
    const [cfg, c, t, r, a] = await Promise.all([
      physicalAPI.getTimeslotConfig(), physicalAPI.listClasses(), physicalAPI.listTeachers(),
      physicalAPI.listRooms(), physicalAPI.listAssignments(),
    ]);
    setConfig(cfg.data); setClasses(c.data); setTeachers(t.data); setRooms(r.data); setAssignments(a.data);
    if (!viewId && c.data.length) setViewId(c.data[0].id);
  };
  useEffect(() => { loadStatic().catch(() => {}); }, []);

  const loadEntries = async () => {
    if (!viewId) { setEntries([]); return; }
    const params = viewMode === 'class' ? { class_id: viewId } : viewMode === 'teacher' ? { teacher_id: viewId } : { room_id: viewId };
    const res = await physicalAPI.timetableEntries(params);
    setEntries(res.data);
  };
  useEffect(() => { loadEntries().catch(() => {}); }, [viewMode, viewId]);

  const onCellClick = (day, start) => {
    if (viewMode !== 'class') { toast.info(fr ? 'Passez en vue "classe" pour ajouter un cours' : 'Switch to class view to add a lesson'); return; }
    setAddForm({ class_id: viewId, subject_id: '', room_id: classes.find((c) => c.id === viewId)?.room_id || '', duration: 1 });
    setAddDialog({ day, start });
  };

  const classSubjects = assignments.filter((a) => a.class_id === (addDialog ? addForm.class_id : viewId));

  const createEntry = async () => {
    if (!addForm.subject_id) { toast.error(fr ? 'Choisissez une matière' : 'Pick a subject'); return; }
    const slot = config?.slot_minutes || 60;
    try {
      await physicalAPI.createEntry({
        class_id: addForm.class_id, subject_id: addForm.subject_id, room_id: addForm.room_id || null,
        day: addDialog.day, start: addDialog.start, end: addDialog.start + slot * addForm.duration,
      });
      toast.success(fr ? 'Cours placé' : 'Lesson placed');
      setAddDialog(null); loadEntries(); 
    } catch (e) {
      const d = e.response?.data?.detail;
      const msgs = d?.conflicts?.map((c) => c.detail).join(' · ') || (typeof d === 'string' ? d : 'Conflit');
      toast.error(`${fr ? 'Conflit' : 'Conflict'} : ${msgs}`);
    }
  };

  const deleteEntry = async () => {
    await physicalAPI.deleteEntry(selectedEntry.id);
    setSelectedEntry(null);
    toast.success(fr ? 'Créneau supprimé' : 'Slot deleted');
    loadEntries();
  };

  const generate = async (apply) => {
    setGenLoading(true);
    try {
      const res = await physicalAPI.generateTimetable(viewMode === 'class' ? viewId : null, apply);
      if (apply) {
        toast.success(fr ? `${res.data.summary.placed_count} créneaux placés` : `${res.data.summary.placed_count} slots placed`);
        setGenReport(null); loadEntries();
      } else {
        setGenReport(res.data);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setGenLoading(false); }
  };

  const viewOptions = viewMode === 'class' ? classes.map((c) => ({ id: c.id, label: c.name }))
    : viewMode === 'teacher' ? teachers.map((t) => ({ id: t.user_id, label: `${t.first_name} ${t.last_name}` }))
    : rooms.map((r) => ({ id: r.id, label: r.name }));

  return (
    <div className="space-y-5" data-testid="timetable-page">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <h1 className="text-2xl font-outfit font-bold text-slate-900">{fr ? 'Emplois du temps' : 'Timetables'}</h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => generate(false)} disabled={genLoading} data-testid="generate-tt-btn">
            <Wand2 className="w-4 h-4 mr-2" />{fr ? 'Générer' : 'Generate'}
          </Button>
          <Button variant="outline" onClick={async () => {
            await physicalAPI.clearTimetable(viewMode === 'class' ? viewId : null);
            toast.success(fr ? 'Emploi du temps vidé (créneaux verrouillés conservés)' : 'Timetable cleared');
            loadEntries();
          }} data-testid="clear-tt-btn">
            <Eraser className="w-4 h-4 mr-2" />{fr ? 'Vider' : 'Clear'}
          </Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="print-tt-btn">
            <Printer className="w-4 h-4 mr-2" />{fr ? 'Imprimer / PDF' : 'Print / PDF'}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-end print:hidden">
        <div>
          <Label className="text-xs">{fr ? 'Vue' : 'View'}</Label>
          <select className="h-10 border border-slate-200 rounded-md px-2 text-sm block" value={viewMode}
            onChange={(e) => { setViewMode(e.target.value); setViewId(''); }} data-testid="tt-viewmode-select">
            <option value="class">{fr ? 'Par classe' : 'By class'}</option>
            <option value="teacher">{fr ? 'Par enseignant' : 'By teacher'}</option>
            <option value="room">{fr ? 'Par salle' : 'By room'}</option>
          </select>
        </div>
        <div>
          <Label className="text-xs">{viewMode === 'class' ? (fr ? 'Classe' : 'Class') : viewMode === 'teacher' ? (fr ? 'Enseignant' : 'Teacher') : (fr ? 'Salle' : 'Room')}</Label>
          <select className="h-10 border border-slate-200 rounded-md px-2 text-sm block min-w-[180px]" value={viewId} onChange={(e) => setViewId(e.target.value)} data-testid="tt-entity-select">
            <option value="">—</option>
            {viewOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </div>
        {viewMode === 'class' && <p className="text-xs text-slate-400 pb-2">{fr ? '💡 Cliquez sur une case vide pour placer un cours, sur un cours pour le gérer' : '💡 Click an empty cell to place a lesson'}</p>}
      </div>

      {config && viewId ? (
        <TimetableGrid entries={entries} config={config} onCellClick={onCellClick} onEntryClick={setSelectedEntry} />
      ) : (
        <p className="text-slate-400 text-sm text-center py-10">{fr ? 'Sélectionnez une classe, un enseignant ou une salle' : 'Select a class, teacher or room'}</p>
      )}

      {/* Add entry dialog */}
      <Dialog open={!!addDialog} onOpenChange={() => setAddDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {fr ? 'Placer un cours' : 'Place a lesson'} — {addDialog && `${DAY_LABELS[fr ? 'fr' : 'en'][addDialog.day]} ${minToStr(addDialog.start)}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{fr ? 'Matière (affectations de la classe)' : 'Subject (class assignments)'}</Label>
              <select className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm" value={addForm.subject_id} onChange={(e) => setAddForm({ ...addForm, subject_id: e.target.value })} data-testid="tt-add-subject-select">
                <option value="">—</option>
                {classSubjects.map((a) => <option key={a.id} value={a.subject_id}>{a.subject_name} ({a.teacher_name})</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{fr ? 'Salle' : 'Room'}</Label>
                <select className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm" value={addForm.room_id} onChange={(e) => setAddForm({ ...addForm, room_id: e.target.value })}>
                  <option value="">—</option>
                  {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div>
                <Label>{fr ? 'Durée (créneaux)' : 'Duration (slots)'}</Label>
                <select className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm" value={addForm.duration} onChange={(e) => setAddForm({ ...addForm, duration: parseInt(e.target.value) })}>
                  {[1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog(null)}>{fr ? 'Annuler' : 'Cancel'}</Button>
            <Button className="bg-emerald-700" onClick={createEntry} data-testid="tt-add-confirm-btn">{fr ? 'Placer' : 'Place'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Entry actions dialog */}
      <Dialog open={!!selectedEntry} onOpenChange={() => setSelectedEntry(null)}>
        <DialogContent className="max-w-sm" data-testid="tt-entry-dialog">
          <DialogHeader><DialogTitle>{selectedEntry?.subject_name}</DialogTitle></DialogHeader>
          {selectedEntry && (
            <div className="text-sm text-slate-600 space-y-1">
              <p>{DAY_LABELS[fr ? 'fr' : 'en'][selectedEntry.day]} · {minToStr(selectedEntry.start)} → {minToStr(selectedEntry.end)}</p>
              <p>{selectedEntry.class_name} · {selectedEntry.teacher_name}{selectedEntry.room_name ? ` · ${selectedEntry.room_name}` : ''}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={async () => {
              await physicalAPI.updateEntry(selectedEntry.id, { locked: !selectedEntry.locked });
              setSelectedEntry(null); loadEntries();
            }}>{selectedEntry?.locked ? (fr ? 'Déverrouiller' : 'Unlock') : (fr ? 'Verrouiller' : 'Lock')}</Button>
            <Button variant="destructive" onClick={deleteEntry} data-testid="tt-delete-entry-btn">
              <Trash2 className="w-4 h-4 mr-2" />{fr ? 'Supprimer' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generation report */}
      <Dialog open={!!genReport} onOpenChange={() => setGenReport(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="tt-generate-report">
          <DialogHeader><DialogTitle>{fr ? 'Proposition d\'emploi du temps' : 'Timetable proposal'}</DialogTitle></DialogHeader>
          <p className="text-emerald-700 font-medium">
            {genReport?.summary?.placed_count} {fr ? 'créneaux placés' : 'slots placed'}
            {genReport?.summary?.unplaced_count > 0 && <span className="text-red-600"> · {genReport.summary.unplaced_count} {fr ? 'non placés' : 'unplaced'}</span>}
          </p>
          {genReport?.unplaced?.length > 0 && (
            <div className="bg-red-50 rounded-lg p-3 space-y-1">
              {genReport.unplaced.map((u, i) => (
                <p key={i} className="text-xs text-red-600"><b>{u.class_name} · {u.subject_name}</b> — {u.reason}</p>
              ))}
            </div>
          )}
          <p className="text-xs text-slate-400">{fr ? 'Appliquer remplace les créneaux non verrouillés existants.' : 'Applying replaces existing non-locked slots.'}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenReport(null)}>{fr ? 'Annuler' : 'Cancel'}</Button>
            <Button className="bg-emerald-700" disabled={genLoading} onClick={() => generate(true)} data-testid="tt-generate-apply-btn">{fr ? 'Appliquer' : 'Apply'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
